import { useEffect, useMemo, useRef, useState, useContext } from "react";
import {
  askBrandAssistant,
  updatedChatContext,
  getUniqueBrands,
} from "../../services/chatAssistantService";
import styles from "./ChatAssistant.module.css";
import { AuthContext } from "../../context/auth-context";
import { useTabContext } from "../../hooks/useTabContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const generateId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const SourcesHover = ({ sources }) => {
  if (!sources?.length) return null;

  return (
    <span className={styles.sourcesWrapper}>
      <span className={styles.sourcesLabel}>Sources ⓘ</span>
      <div className={styles.sourcesTooltip}>
        {sources.map((url) => (
          <div key={url}>{url}</div>
        ))}
      </div>
    </span>
  );
};

const ChatAssistant = () => {
  const { user } = useContext(AuthContext);
  const userId = user?.user_role?.user_id;
  const [loadDefaultContext, setLoadDefaultContext] = useState(false);
  const hasFetchedLatestExecution = useRef(false);
  const fabRef = useRef(null);
  // Consolidated UI state
  const [uiState, setUiState] = useState({
    isOpen: false,
    isSending: false,
    isLoadingContext: false,
    inputValue: "",
  });
  const {
    tabName,
    execution_date: tabExecutionDate,
    brand: tabBrand,
    brandContext: tabBrandContext,
    category: tabCategory,
    llmModel: tabLlmModel,
  } = useTabContext();
  console.log(
    "monitoringValues",
    tabName,
    tabExecutionDate,
    tabBrand,
    tabBrandContext,
    tabCategory,
    tabLlmModel,
  );
  console.log("tabExecutionDate", tabExecutionDate);
  // Consolidated chat state
  const [chatState, setChatState] = useState({
    messages: [
      {
        id: "welcome",
        role: "assistant",
        content:
          "Hi! I am your revere copilot. Ask about brand analysis and monitoring and I will answer using your workspace data.",
      },
    ],
    brandContext: null,
    conversationId: null,
    phase: "",
    selectedExecutionDate: null,
    selectedBrand: tabBrand,
  });
  const hasInitializedRef = useRef(false);
  const messagesEndRef = useRef(null);
  useEffect(() => {
    if (!uiState.isOpen) return;

    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [chatState.messages, uiState.isSending, uiState.isOpen]);

  useEffect(() => {
    if (!tabBrand || hasInitializedRef.current) return;

    hasInitializedRef.current = true;
    setChatState((prev) => ({
      ...prev,
      selectedBrand: tabBrand,
    }));
  }, [tabBrand]);

  console.log("Selected Brand", chatState.selectedBrand);

  // Add this useEffect after your existing useEffect hooks
  useEffect(() => {
    const fetchLatestExecutionDate = async () => {
      if (
        !chatState.selectedBrand ||
        !userId ||
        hasFetchedLatestExecution.current
      ) {
        return;
      }

      hasFetchedLatestExecution.current = true;

      setUiState((prev) => ({
        ...prev,
        isSending: true,
        isLoadingContext: true,
      }));

      try {
        const { execution_date, brandContext, conversationId } =
          await updatedChatContext(userId, chatState.selectedBrand);

        setChatState((prev) => ({
          ...prev,
          selectedExecutionDate: execution_date,
          conversationId,
          brandContext,
        }));
      } catch (error) {
        console.error("Error fetching latest execution date:", error);

        setChatState((prev) => ({
          ...prev,
          messages: [
            ...prev.messages,
            {
              id: generateId(),
              role: "assistant",
              content:
                "I encountered an issue loading the latest execution date. Please try again.",
            },
          ],
        }));
      } finally {
        setLoadDefaultContext(true);
        setUiState((prev) => ({
          ...prev,
          isSending: false,
          isLoadingContext: false,
        }));
      }
    };

    fetchLatestExecutionDate();
  }, [chatState.selectedBrand, userId]);

  const latestControllerRef = useRef(null);
  const dragOffsetRef = useRef(null);
  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") return { x: 24, y: 24 };
    return {
      x: window.innerWidth - 380,
      y: 160,
    };
  });
  const clampedPosition = useMemo(() => {
    if (typeof window === "undefined") return position;
    const maxX = window.innerWidth - 320;
    const maxY = window.innerHeight - 140;
    return {
      x: Math.min(Math.max(position.x, 12), maxX),
      y: Math.min(Math.max(position.y, 12), maxY),
    };
  }, [position]);
  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      latestControllerRef.current?.abort?.();
    };
  }, []);
  const handleDragStart = (event) => {
    if (!uiState.isOpen) return;
    const point = event.touches?.[0] || event;
    dragOffsetRef.current = {
      x: point.clientX - clampedPosition.x,
      y: point.clientY - clampedPosition.y,
    };
    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", handleDragEnd);
    window.addEventListener("touchmove", handleDragMove);
    window.addEventListener("touchend", handleDragEnd);
  };
  const handleDragMove = (event) => {
    if (!dragOffsetRef.current) return;
    const point = event.touches?.[0] || event;
    setPosition({
      x: point.clientX - dragOffsetRef.current.x,
      y: point.clientY - dragOffsetRef.current.y,
    });
  };
  const handleDragEnd = () => {
    dragOffsetRef.current = null;
    window.removeEventListener("mousemove", handleDragMove);
    window.removeEventListener("mouseup", handleDragEnd);
    window.removeEventListener("touchmove", handleDragMove);
    window.removeEventListener("touchend", handleDragEnd);
  };

  const handleToggle = () => {
    // Close
    if (uiState.isOpen) {
      setUiState((prev) => ({ ...prev, isOpen: false }));
      return;
    }
  
    // Open → reposition chat ABOVE FAB
    if (fabRef.current) {
      const rect = fabRef.current.getBoundingClientRect();
  
      const chatWidth = 360;
      const chatHeight = window.innerHeight * 0.7;
  
      const newX = Math.min(
        rect.right - chatWidth,
        window.innerWidth - chatWidth - 12
      );
  
      const newY = Math.max(
        rect.top - chatHeight - 12,
        12
      );
  
      setPosition({ x: newX, y: newY });
    }
  
    setUiState((prev) => ({
      ...prev,
      isOpen: true,
      isLoadingContext: true,
    }));
  };
  

  console.log("uistate", uiState);
  console.log("chatState", chatState);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    await sendMessage(uiState.inputValue);
  };
  const sendMessage = async (text, execution_date, brand) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Use provided execution_date/brand or fall back to selected ones
    console.log("Chat State", chatState);
    const execDate = execution_date || chatState.selectedExecutionDate;
    const brandName = brand || chatState.selectedBrand;
    const newUserMessage = {
      id: generateId(),
      role: "user",
      content: trimmed,
    };
    setChatState((prev) => ({
      ...prev,
      messages: [...prev.messages, newUserMessage],
    }));
    setUiState((prev) => ({
      ...prev,
      inputValue: "",
      isSending: true,
      isOpen: true,
    }));
    const controller = new AbortController();
    latestControllerRef.current = controller;
    try {
      const response = await askBrandAssistant(
        [...chatState.messages, newUserMessage],
        execDate,
        brandName,
        controller,
        userId,
        chatState.brandContext,
        chatState.phase,
        chatState.conversationId,
      );
      console.log("Chat API response:", response);
      const {
        answer,
        options,
        conversationId,
        currentBrandContext,
        phase,
        sources = [],
      } = response;
      // Update chat state with response
      setChatState((prev) => {
        const updates = {};
        if (currentBrandContext) {
          updates.brandContext = currentBrandContext;
        }
        if (phase === "CONFIRMED") {
          updates.phase = "CONFIRMED";
          updates.selectedExecutionDate = null;
        } else if (phase === "SELECTION") {
          updates.phase = "SELECTION";
        }
        if (conversationId) {
          updates.conversationId = conversationId;
        }
        if (execution_date && brand) {
          updates.selectedExecutionDate = execution_date;
          updates.selectedBrand = brand;
        }
        // Render assistant message
        const newAssistantMessage = {
          id: generateId(),
          role: "assistant",
          options: options,
          sources,
          content:
            answer?.answer ??
            answer ??
            "I could not find anything useful just now. Try rephrasing or narrowing the question.",
        };
        return {
          ...prev,
          ...updates,
          messages: [...prev.messages, newAssistantMessage],
        };
      });
    } catch (error) {
      const newAssistantMessage = {
        id: generateId(),
        role: "assistant",
        content:
          "I ran into an issue reaching the assistant. Please try again or adjust your question.",
      };
      setChatState((prev) => ({
        ...prev,
        messages: [...prev.messages, newAssistantMessage],
      }));
      console.error(error);
    } finally {
      setUiState((prev) => ({ ...prev, isSending: false }));
      latestControllerRef.current = null;
    }
  };
  const handleSuggestion = (suggestion, execution_date, brand) => {
    setChatState((prev) => ({ ...prev, phase: null }));
    setUiState((prev) => ({ ...prev, inputValue: suggestion }));
    sendMessage(suggestion, execution_date, brand);
  };
  const handleClearContext = async () => {
    setChatState((prev) => ({
      ...prev,
      selectedExecutionDate: null,
      selectedBrand: null,
      conversationId: null,
      brandContext: null,
      phase: "SELECTION",
    }));

    setUiState((prev) => ({
      ...prev,
      isSending: true,
      isLoadingContext: true,
    }));

    console.log("Chat Phase", chatState);

    try {
      const { answer, options, phase } = await getUniqueBrands(userId);
      // 3. Update state with API results
      setChatState((prev) => ({
        ...prev,
        phase,
        messages: [
          ...prev.messages,
          {
            id: generateId(),
            role: "assistant",
            content: answer,
            options: options,
          },
        ],
      }));
    } catch (error) {
      const newAssistantMessage = {
        id: generateId(),
        role: "assistant",
        content: "I ran into an issue in geting the avaliable Brand's.",
      };
      setChatState((prev) => ({
        ...prev,
        messages: [...prev.messages, newAssistantMessage],
      }));
      console.error(error);
    } finally {
      setUiState((prev) => ({
        ...prev,
        isSending: false,
        isLoadingContext: false,
      }));
    }
  };

  const renderOptions = (options) => {
    if (!options || options.length == 0) {
      return <></>;
    }
    return (
      <div>
        <div className={styles.quickCard}>
          <div className={styles.quickList}>
            {options.map((opt) => (
              <button
                key={opt.execution_date}
                className={styles.quickChip}
                onClick={() => {
                  handleSuggestion(
                    opt.execution_date.length > 5
                      ? opt.brandNames[0] + " " + opt.execution_date
                      : opt.brandNames[0],
                    opt.execution_date.length > 5 ? opt.execution_date : null,
                    opt.brandNames[0] ? opt.brandNames[0] : null,
                  );
                }}
              >
                <div className={styles.dateText}>
                  {opt.execution_date.length > 5 ? opt.execution_date : ""}
                </div>
                <div className={styles.brandText}>
                  {opt.brandNames.join(" • ")}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };
  return (
    <>
      {loadDefaultContext && (
        <button
          ref={fabRef}
          type="button"
          className={`${styles.fab} ${uiState.isOpen ? styles.fabActive : ""}`}
          onClick={handleToggle}
        >
          <div className={styles.fabGlow} />
          <span className={styles.fabIcon}>💬</span>
        </button>
      )}

      {uiState.isOpen && (
        <div
          className={styles.chatShell}
          style={{
            top: `${clampedPosition.y}px`,
            left: `${clampedPosition.x}px`,
          }}
        >
          <div
            className={styles.header}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <div>
              <div className={styles.title}>Revere Copilot</div>
              <div className={styles.subtitle}>
                Ask about Brand Analysis and Monitoring.
              </div>
            </div>
            <div className={styles.headerActions}>
              {uiState.isSending ? (
                <div className={styles.statusPill}>Thinking...</div>
              ) : (
                <div className={styles.statusPillReady}>Ready</div>
              )}
              <button
                className={styles.closeButton}
                onClick={() =>
                  setUiState((prev) => ({ ...prev, isOpen: false }))
                }
                aria-label="Close assistant"
              >
                ✕
              </button>
            </div>
          </div>
          {chatState.selectedBrand && chatState.selectedExecutionDate && (
            <div className={styles.contextBar}>
              {chatState.selectedBrand} brand execution highlights from{" "}
              {chatState.selectedExecutionDate}.
              <button
                className={styles.clearContextBtn}
                onClick={handleClearContext}
              >
                ✕
              </button>
            </div>
          )}

          <div className={styles.messages}>
            {chatState.messages.map((message) => (
              <div
                key={message.id}
                className={`${styles.message} ${
                  message.role === "user" ? styles.user : styles.assistant
                }`}
              >
                <div className={styles.messageMeta}>
                  {message.role === "user" ? "You" : "Copilot"}
                </div>
                <div className={styles.messageBody}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>

                  {message.sources?.length > 0 && (
                    <SourcesHover sources={message.sources} />
                  )}

                  {renderOptions(message.options)}
                </div>
              </div>
            ))}
            {uiState.isSending && (
              <div className={`${styles.message} ${styles.assistant}`}>
                <div className={styles.messageMeta}>Copilot</div>
                <div className={styles.typing}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <form className={styles.inputPanel} onSubmit={handleSubmit}>
            <label className={styles.inputLabel}>User input</label>
            <div className={styles.inputRow}>
              <input
                value={uiState.inputValue}
                onChange={(event) =>
                  setUiState((prev) => ({
                    ...prev,
                    inputValue: event.target.value,
                  }))
                }
                className={styles.input}
                placeholder="Ask anything about your monitoring, audit, or insights data..."
              />
              <button
                type="submit"
                className={styles.sendButton}
                disabled={uiState.isSending || !uiState.inputValue.trim()}
              >
                Send
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
export default ChatAssistant;
